﻿// js/Views/Account/account-ui.js
/* =========================================================
   ACCOUNT UI
   Path: /wwwroot/js/Views/Account/account-ui.js
   
   UI state management, section switching, focus management,
   button states, form locking, and password toggle.
   
   Dependencies: 
   - /wwwroot/js/_shared/_utils.js (load first)
   - /wwwroot/js/Views/Account/account-validation.js (load second)
   
   Load Order: 3rd
   
   FIX v2: Completely rewrote form locking to prevent stale state issues
   - Lock state is now tracked per-form using data attributes
   - Original states are stored on the form element itself
   - Added safety timeout to auto-unlock after 30 seconds
   - forceUnlock now properly resets ALL interactive elements
   ========================================================= */

(function () {
    "use strict";

    // Get utilities from shared namespace
    var utils = window.AppUtils || {};
    var qs = utils.qs || window.qs;
    var qsa = utils.qsa || window.qsa;
    var setText = utils.setText;
    var focusIfPossible = utils.focusIfPossible;

    // Get validation functions
    var validation = (window.AccountApp && window.AccountApp.validation) || {};
    var clearAllValidation = validation.clearAllValidation;
    var initPasswordRequirements = validation.initPasswordRequirements;

    // Ensure namespace exists
    window.AccountApp = window.AccountApp || {};

    // i18n strings (from window.i18n.strings with English fallbacks)
    var _s = (window.i18n && window.i18n.strings) || {};

    // ---------------------------
    // State Variables
    // ---------------------------

    /**
     * Current active section
     */
    var currentSection = (window.__INITIAL_SECTION__ ||
        (window.ACCOUNT && window.ACCOUNT.initialSection) ||
        "gate");

    /**
     * Processing flag to prevent double submissions
     * Now uses a counter to handle edge cases
     */
    var processingCount = 0;

    /**
     * Safety timeout ID for auto-unlock
     */
    var safetyTimeoutId = null;

    /**
     * Safety timeout duration (30 seconds)
     */
    var SAFETY_TIMEOUT_MS = 30000;

    // Check URL parameters for reset password flow
    var urlParams = new URLSearchParams(window.location.search);
    var hasResetCode = urlParams.has('code');
    var hasResetEmail = urlParams.has('email');

    if (hasResetCode && hasResetEmail) {
        currentSection = "reset-password";
    }

    // Check for login errors from server
    var hasLoginErrors = (window.ACCOUNT && window.ACCOUNT.hasLoginErrors === true) ||
        (window.ACCOUNT && window.ACCOUNT.hasLoginErrors === "true");
    if (hasLoginErrors && currentSection === "gate") {
        currentSection = "login";
    }

    // ---------------------------
    // State Getters/Setters
    // ---------------------------

    function getCurrentSection() {
        return currentSection;
    }

    function setCurrentSection(section) {
        currentSection = section;
    }

    function getIsProcessing() {
        return processingCount > 0;
    }

    function setIsProcessing(value) {
        if (value) {
            processingCount++;
        } else {
            processingCount = Math.max(0, processingCount - 1);
        }
    }

    // ---------------------------
    // Form Locking (REWRITTEN v2)
    // ---------------------------

    /**
     * Lock a form during submission - disables all inputs and buttons,
     * adds visual overlay to prevent interaction.
     * 
     * IMPORTANT: This function now stores state on the form element itself
     * to prevent closure-related bugs with stale state.
     * 
     * @param {Element} form - Form element or parent container to lock
     * @returns {Function} Unlock function to call when done
     */
    function lockForm(form) {
        if (!form) {
            console.warn('[lockForm] No form provided');
            return function () { };
        }

        // Find the form card container for the overlay
        var formCard = form.closest('.form-card') || form.closest('.auth-card') || form;

        // Generate a unique lock ID for this lock operation
        var lockId = 'lock_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);

        // If already locked, force unlock first
        if (formCard.classList.contains('is-locked')) {
            console.warn('[lockForm] Form was already locked - forcing unlock first');
            forceUnlockElement(formCard);
        }

        // Clear any existing safety timeout
        if (safetyTimeoutId) {
            clearTimeout(safetyTimeoutId);
            safetyTimeoutId = null;
        }

        // Mark as processing
        processingCount++;

        // Add locked class and store lock ID
        formCard.classList.add('is-locked');
        formCard.setAttribute('data-lock-id', lockId);

        // Disable all interactive elements and store their CURRENT state
        // We store on the element itself to avoid closure issues
        var interactiveElements = qsa('input, button, select, textarea, a', formCard);

        interactiveElements.forEach(function (el) {
            // Store current state as data attributes (won't get stale)
            el.setAttribute('data-was-disabled', el.disabled ? 'true' : 'false');
            var currentTabIndex = el.getAttribute('tabindex');
            if (currentTabIndex !== null) {
                el.setAttribute('data-was-tabindex', currentTabIndex);
            }
            // Now disable
            el.disabled = true;
            el.setAttribute('tabindex', '-1');
        });

        // Create overlay element
        var overlay = document.createElement('div');
        overlay.className = 'form-lock-overlay';
        overlay.setAttribute('aria-hidden', 'true');
        overlay.setAttribute('data-lock-id', lockId);
        formCard.appendChild(overlay);

        // Set safety timeout to auto-unlock if something goes wrong
        safetyTimeoutId = setTimeout(function () {
            console.warn('[lockForm] Safety timeout reached - forcing unlock');
            forceUnlockElement(formCard);
        }, SAFETY_TIMEOUT_MS);

        // Track if this unlock has been called
        var unlockCalled = false;

        // Return unlock function
        return function unlock() {
            // Prevent double-unlock
            if (unlockCalled) {
                console.warn('[unlock] Already unlocked, ignoring duplicate call');
                return;
            }
            unlockCalled = true;


            // Clear safety timeout
            if (safetyTimeoutId) {
                clearTimeout(safetyTimeoutId);
                safetyTimeoutId = null;
            }

            // Verify this is still the active lock (prevent stale unlock calls)
            var currentLockId = formCard.getAttribute('data-lock-id');
            if (currentLockId !== lockId) {
                console.warn('[unlock] Lock ID mismatch - this unlock is stale. Expected:', lockId, 'Current:', currentLockId);
                // Still decrement processing count
                processingCount = Math.max(0, processingCount - 1);
                return;
            }

            // Remove overlay (find by lock ID to be safe)
            var overlays = qsa('.form-lock-overlay[data-lock-id="' + lockId + '"]', formCard);
            overlays.forEach(function (o) {
                if (o.parentNode) {
                    o.parentNode.removeChild(o);
                }
            });

            // Also remove any orphaned overlays (safety)
            var allOverlays = qsa('.form-lock-overlay', formCard);
            allOverlays.forEach(function (o) {
                if (o.parentNode) {
                    o.parentNode.removeChild(o);
                }
            });

            // Restore original states from data attributes
            var elements = qsa('input, button, select, textarea, a', formCard);
            elements.forEach(function (el) {
                // Restore disabled state
                var wasDisabled = el.getAttribute('data-was-disabled');
                if (wasDisabled !== null) {
                    el.disabled = (wasDisabled === 'true');
                    el.removeAttribute('data-was-disabled');
                } else {
                    // No stored state - default to enabled
                    el.disabled = false;
                }

                // Restore tabindex
                var wasTabIndex = el.getAttribute('data-was-tabindex');
                if (wasTabIndex !== null) {
                    el.setAttribute('tabindex', wasTabIndex);
                    el.removeAttribute('data-was-tabindex');
                } else {
                    el.removeAttribute('tabindex');
                }
            });

            // Remove locked class and lock ID
            formCard.classList.remove('is-locked');
            formCard.removeAttribute('data-lock-id');

            // Decrement processing count
            processingCount = Math.max(0, processingCount - 1);

        };
    }

    /**
     * Force unlock a specific element
     * @param {Element} formCard - The form card element to unlock
     */
    function forceUnlockElement(formCard) {
        if (!formCard) return;


        // Clear safety timeout
        if (safetyTimeoutId) {
            clearTimeout(safetyTimeoutId);
            safetyTimeoutId = null;
        }

        // Remove ALL overlays
        var overlays = qsa('.form-lock-overlay', formCard);
        overlays.forEach(function (o) {
            if (o.parentNode) {
                o.parentNode.removeChild(o);
            }
        });

        // Re-enable all interactive elements (ignore stored state - force enable)
        var elements = qsa('input, button, select, textarea, a', formCard);
        elements.forEach(function (el) {
            // Force enable (don't trust stored state in force unlock)
            el.disabled = false;

            // Clean up data attributes
            el.removeAttribute('data-was-disabled');
            el.removeAttribute('data-was-tabindex');
            el.removeAttribute('tabindex');
        });

        // Remove locked class and lock ID
        formCard.classList.remove('is-locked');
        formCard.removeAttribute('data-lock-id');
    }

    /**
     * Emergency unlock - call if something goes wrong
     * Clears ALL locked form states on the page
     */
    function forceUnlock() {

        // Clear safety timeout
        if (safetyTimeoutId) {
            clearTimeout(safetyTimeoutId);
            safetyTimeoutId = null;
        }

        // Find and unlock all locked form cards
        var lockedForms = qsa('.is-locked');
        lockedForms.forEach(function (formCard) {
            forceUnlockElement(formCard);
        });

        // Reset processing count
        processingCount = 0;

    }

    // ---------------------------
    // Focus Management
    // ---------------------------

    /**
     * Set focus intelligently based on section and form state
     * @param {string} section - The section being shown
     */
    function setSmartFocus(section) {
        setTimeout(function () {
            if (section === 'login') {
                var emailField = qs('#login-form input[name="Login.Email"]');
                var passwordField = qs('#login-form input[name="Login.Password"]');

                // If email is filled, focus password; otherwise focus email
                if (emailField && emailField.value && emailField.value.trim() !== '') {
                    if (passwordField) passwordField.focus();
                } else {
                    if (emailField) emailField.focus();
                }
            } else if (section === 'register') {
                var regEmailField = qs('#register-form input[name="Register.Email"]');
                var displayNameField = qs('#register-form input[name="Register.DisplayName"]');
                var regPasswordField = qs('#register-form input[name="Register.Password"]');

                // Focus first empty field in order: email, display name, password
                if (regEmailField && regEmailField.value && regEmailField.value.trim() !== '') {
                    if (displayNameField && !displayNameField.value) {
                        displayNameField.focus();
                    } else if (regPasswordField && !regPasswordField.value) {
                        regPasswordField.focus();
                    } else {
                        // All filled - find first empty field
                        var fields = [
                            '#register-form input[name="Register.Email"]',
                            '#register-form input[name="Register.DisplayName"]',
                            '#register-form input[name="Register.Password"]',
                            '#register-form input[name="Register.ConfirmPassword"]'
                        ];

                        for (var i = 0; i < fields.length; i++) {
                            var field = qs(fields[i]);
                            if (field && !field.value) {
                                field.focus();
                                return;
                            }
                        }
                    }
                } else {
                    if (regEmailField) regEmailField.focus();
                }
            } else {
                // Default focus targets for other sections
                var focusTargets = {
                    gate: "#email-gate-input",
                    forgot: "#forgot-email",
                    'reset-password': "#reset-password-input"
                };

                if (focusTargets[section]) {
                    focusIfPossible(focusTargets[section]);
                }
            }
        }, 100);
    }

    // ---------------------------
    // Button State Management
    // ---------------------------

    /**
     * Set button visual state (normal, loading, success)
     * @param {Element} button - Button element
     * @param {string} state - 'loading', 'normal', or 'success'
     * @param {string} [text] - Optional text to display
     */
    function setButtonState(button, state, text) {
        if (!button) return;

        var originalText = button.getAttribute('data-original-text') || button.innerHTML;

        if (state === 'loading') {
            // Save original text if not already saved
            if (!button.getAttribute('data-original-text')) {
                button.setAttribute('data-original-text', originalText);
            }
            button.disabled = true;
            button.innerHTML = '<span class="spinner"></span> ' + (text || _s.processing || 'Processing…');
            button.classList.add('checking');

        } else if (state === 'normal') {
            button.disabled = false;
            button.innerHTML = originalText;
            button.classList.remove('checking');

        } else if (state === 'success') {
            button.disabled = false;
            button.innerHTML =
                '<svg class="success-check-inline" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">' +
                '<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>' +
                '<polyline points="22 4 12 14.01 9 11.01"></polyline>' +
                '</svg> ' + (text || _s.accSuccess || 'Success!');
            button.classList.remove('checking');
        }
    }

    /**
     * Prevent multiple form submissions
     * @param {Element} form - Form element to protect
     */
    function preventMultipleSubmissions(form) {
        if (!form) return;

        form.addEventListener('submit', function (e) {
            if (getIsProcessing()) {
                e.preventDefault();
                return false;
            }
        });
    }

    // ---------------------------
    // Section Switching
    // ---------------------------

    /**
     * Section title and subtitle mappings
     */
    var sectionTitles = {
        gate: [_s.accGateTitle || "Let's find your account", _s.accGateSubtitle || "Enter your email to continue"],
        login: [_s.accLoginTitle || "Welcome Back", _s.accLoginSubtitle || "Sign in to continue"],
        register: [_s.accRegisterTitle || "Get Started", _s.accRegisterSubtitle || "Create your new account"],
        forgot: [_s.accForgotTitle || "Reset Password", _s.accForgotSubtitle || "We'll send you a reset link"],
        'forgot-confirmation': [_s.accForgotConfirmTitle || "Check Your Email", _s.accForgotConfirmSubtitle || "Reset instructions sent"],
        'reset-password': [_s.accResetTitle || "Create New Password", _s.accResetSubtitle || "Choose a strong password"],
        'reset-confirmation': [_s.accResetCompleteTitle || "Password Reset Complete", _s.accResetCompleteSubtitle || "You can now sign in"],
        // AB#352: the ended panel carries its own heading and dates, so the card header is blanked
        // rather than left saying "Let's find your account" above an event that is over.
        closed: ['', '']
    };

    /**
     * Show a specific section and hide others
     * @param {string} section - Section to show
     * @param {boolean} [preserveErrors=false] - Keep validation errors visible
     */
    function showSection(section, preserveErrors) {
        // Prevent leaving reset-password if URL has reset params
        if (currentSection === "reset-password" && section !== "reset-password") {
            var params = new URLSearchParams(window.location.search);
            if (params.has('code') && params.has('email')) {
                return;
            }
        }

        // Force unlock any locked forms when switching sections
        forceUnlock();

        currentSection = section;

        // Section elements
        var sections = {
            gate: qs("#email-gate"),
            login: qs("#login-form"),
            register: qs("#register-form"),
            forgot: qs("#forgot-form"),
            'forgot-confirmation': qs("#forgot-confirmation"),
            'reset-password': qs("#reset-password-form"),
            'reset-confirmation': qs("#reset-confirmation"),
            // AB#352: the ended-event panel on a closed event. It is rendered hidden alongside the
            // other sections so the gate can reveal it once the email turns out not to be a
            // sponsor's, instead of the server deciding before anyone has identified themselves.
            closed: qs("#event-closed-section")
        };

        // Show/hide sections
        for (var key in sections) {
            if (sections.hasOwnProperty(key) && sections[key]) {
                sections[key].style.display = (key === section) ? "block" : "none";
            }
        }

        // Clear validation unless preserving or showing confirmation
        if (!preserveErrors && section !== "forgot-confirmation" && section !== "reset-confirmation") {
            for (var sectionKey in sections) {
                if (sections.hasOwnProperty(sectionKey) && sections[sectionKey] && clearAllValidation) {
                    clearAllValidation(sections[sectionKey]);
                }
            }
        }

        // Update header text
        var titles = sectionTitles[section] || ["", ""];
        setText(qs("#form-title"), titles[0]);
        setText(qs("#form-subtitle"), titles[1]);

        // Set focus
        setSmartFocus(section);

        // Initialize password requirements UI when showing register or reset sections
        if ((section === 'register' || section === 'reset-password') && initPasswordRequirements) {
            setTimeout(function () {
                initPasswordRequirements();
            }, 50);
        }
    }

    // Expose showSection globally for Razor onclick handlers
    window.showSection = showSection;

    // ---------------------------
    // Password Toggle
    // ---------------------------

    /**
     * Toggle password visibility
     * @param {Element} button - Toggle button element
     */
    function togglePassword(button) {
        var wrap = button && button.closest(".password-wrapper");
        var input = wrap && qs("input", wrap);
        if (!input) return;

        var newType = input.getAttribute("type") === "password" ? "text" : "password";
        input.setAttribute("type", newType);

        var svg = qs("svg", button);
        if (!svg) return;

        if (newType === "text") {
            // Show "hide" icon
            svg.innerHTML =
                '<path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"></path>' +
                '<line x1="1" y1="1" x2="23" y2="23"></line>';
        } else {
            // Show "show" icon
            svg.innerHTML =
                '<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>' +
                '<circle cx="12" cy="12" r="3"></circle>';
        }
    }

    // Expose togglePassword globally for Razor onclick handlers
    window.togglePassword = togglePassword;

    // ---------------------------
    // Export to namespace
    // ---------------------------

    window.AccountApp.ui = {
        // State
        getCurrentSection: getCurrentSection,
        setCurrentSection: setCurrentSection,
        getIsProcessing: getIsProcessing,
        setIsProcessing: setIsProcessing,

        // Form Locking
        lockForm: lockForm,
        forceUnlock: forceUnlock,

        // Functions
        setSmartFocus: setSmartFocus,
        setButtonState: setButtonState,
        preventMultipleSubmissions: preventMultipleSubmissions,
        showSection: showSection,
        togglePassword: togglePassword
    };

})();